import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertTriangle, Briefcase, Plane, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function CustomerDetailModal({ result, onClose }) {
  const { customer, choices, csat, timing, wasOptimal, timing_data, csat_breakdown, optimal_suggestions } = result;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <h2 className="text-xl font-semibold text-gray-50">
              {customer.profile.name} - Detailed Analysis
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-50 hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Customer Profile */}
            <div>
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                Customer Profile
              </h3>
              <div className="bg-slate-800 rounded-lg p-5">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={customer.profile.avatar}
                    alt={customer.profile.name}
                    className="w-16 h-16 rounded-full border-2 border-white/10"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-lg font-semibold text-gray-50">
                        {customer.profile.name}
                      </h4>
                      <Badge
                        variant={
                          customer.profile.tier === 'VIP'
                            ? 'default'
                            : customer.profile.tier === 'Business'
                              ? 'secondary'
                              : 'outline'
                        }
                        className={
                          customer.profile.tier === 'VIP'
                            ? 'bg-purple-500/20 text-purple-400 border-purple-500/50'
                            : customer.profile.tier === 'Business'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                              : 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                        }
                      >
                        {customer.profile.tier}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Briefcase className="w-4 h-4" />
                      <span>{customer.profile.title}</span>
                      {customer.profile.company && (
                        <>
                          <span>•</span>
                          <span>{customer.profile.company}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Original Flight */}
                <div className="border-t border-white/10 pt-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Plane className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-300">Original Flight</span>
                  </div>
                  <div className="pl-6 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Route:</span>
                      <span className="font-mono text-gray-50">
                        {customer.trip.from} → {customer.trip.to}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Flight:</span>
                      <span className="font-mono text-gray-50">
                        {customer.trip.original.airline} {customer.trip.original.flight}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Departure:</span>
                      <span className="font-mono text-gray-50">{customer.trip.original.departs}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Class:</span>
                      <span className="text-gray-50 capitalize">{customer.trip.original.class}</span>
                    </div>
                  </div>
                </div>

                {/* Trip Purpose */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-300 block mb-1">
                        Trip Purpose
                      </span>
                      <p className="text-sm text-gray-400">{customer.trip.purpose}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your choices */}
            <div>
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
                Your Choices
              </h3>
              <div className="space-y-2">
                {choices.transport && (
                  <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                    {choices.transport.recommended ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-50">Transport: {choices.transport.name}</p>
                      <p className="text-xs text-gray-500">
                        Cost: €{choices.transport.cost} • CSAT: +{choices.transport.csat}
                      </p>
                    </div>
                  </div>
                )}

                {choices.communication && (
                  <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                    {choices.communication.recommended ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-50">
                        Communication: {choices.communication.name}
                      </p>
                      <p className="text-xs text-gray-500">CSAT: +{choices.communication.csat}</p>
                    </div>
                  </div>
                )}

                {choices.accommodation && (
                  <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                    {choices.accommodation.recommended ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-50">
                        Accommodation: {choices.accommodation.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Cost: €{choices.accommodation.cost} • CSAT: +{choices.accommodation.csat}
                      </p>
                    </div>
                  </div>
                )}

                {choices.compensation && (
                  <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                    {choices.compensation.recommended ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-50">
                        Compensation: {choices.compensation.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Cost: €{choices.compensation.cost} • CSAT: +{choices.compensation.csat}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timing breakdown */}
            {timing_data && (
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
                  Response Timing
                </h3>
                <div className="bg-slate-800 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Crisis occurred:</span>
                    <span className="font-mono text-gray-50">{timing_data.crisis_time}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">You contacted:</span>
                    <span
                      className={`font-mono ${
                        timing === 'proactive'
                          ? 'text-emerald-400'
                          : timing === 'reactive'
                            ? 'text-blue-400'
                            : 'text-red-400'
                      }`}
                    >
                      {timing_data.contact_time} ({timing_data.delay_minutes} min){' '}
                      {timing === 'proactive' ? '⚡' : timing === 'reactive' ? '✓' : '❌'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Solution confirmed:</span>
                    <span className="font-mono text-gray-50">
                      {timing_data.confirm_time} ({timing_data.total_time} total)
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* CSAT calculation */}
            {csat_breakdown && (
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
                  CSAT Calculation
                </h3>
                <div className="bg-slate-800 rounded-lg p-4 font-mono text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Base CSAT:</span>
                    <span className="text-gray-50">50</span>
                  </div>
                  {choices.transport && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ Transport:</span>
                      <span className="text-emerald-400">+{choices.transport.csat}</span>
                    </div>
                  )}
                  {choices.communication && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ Communication:</span>
                      <span className="text-emerald-400">+{choices.communication.csat}</span>
                    </div>
                  )}
                  {choices.accommodation && choices.accommodation.csat > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ Accommodation:</span>
                      <span className="text-emerald-400">+{choices.accommodation.csat}</span>
                    </div>
                  )}
                  {choices.compensation && choices.compensation.csat > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ Compensation:</span>
                      <span className="text-emerald-400">+{choices.compensation.csat}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between">
                    <span className="text-gray-400">Subtotal:</span>
                    <span className="text-gray-50">{csat_breakdown.subtotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      × Tier multiplier ({customer.profile.tier}):
                    </span>
                    <span className="text-gray-50">×{csat_breakdown.tier_multiplier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">× Time factor:</span>
                    <span className="text-gray-50">×{csat_breakdown.time_factor}</span>
                  </div>
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-gray-50">Final CSAT:</span>
                    <span className="text-emerald-400">{csat}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Optimal comparison */}
            <div>
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
                Optimal Solution
              </h3>
              {wasOptimal ? (
                <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                    <p className="text-emerald-400 font-medium">
                      You chose the optimal path! Well done.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium mb-2">There was a better solution:</p>
                      {optimal_suggestions && optimal_suggestions.length > 0 ? (
                        <ul className="text-sm text-gray-300 space-y-1">
                          {optimal_suggestions.map((suggestion, idx) => (
                            <li key={idx}>• {suggestion}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-300">
                          Consider reviewing recommended options for this customer tier.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/10 bg-slate-800">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
